// eslint-disable-next-line no-unused-vars
import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CareGiverContext } from "../context/CareGiverContext";
import { AppContext } from "../context/AppContext";
import { caregiverNetAmount } from "../utils/paymentDisplay";
import {
  Users,
  Calendar,
  Wallet,
  MapPin,
  CheckCircle,
  AlertCircle,
  Briefcase,
  FileText,
  ChevronRight,
  MoreHorizontal,
  History,
  Phone,
} from "lucide-react";

const CaregiverDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    cToken,
    dashData,
    getDashData,
    contractData,
    getContractData,
    payoutHistory,
    getPayoutHistory,
  } = useContext(CareGiverContext);
  const { slotDateFormat, currency } = useContext(AppContext);

  useEffect(() => {
    if (cToken) {
      getDashData();
      getContractData();
      getPayoutHistory();
    }
  }, [cToken]);

  if (!dashData) {
    return (
      <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-background dark:bg-dark-background text-text dark:text-dark-text">
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full mb-4"></div>
            <p className="text-gray-500 dark:text-dark-muted">
              {t("dashboard.loading")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const { profileData } = dashData;

  //  Null check for profileData
  if (!profileData) {
    return (
      <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-background dark:bg-dark-background text-text dark:text-dark-text">
        <div className="flex items-center justify-center h-96">
          <div className="animate-pulse text-center">
            <div className="w-16 h-16 mx-auto bg-gray-200 dark:bg-gray-700 rounded-full mb-4"></div>
            <p className="text-gray-500 dark:text-dark-muted">
              {t("dashboard.loadingProfile")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Status badge configuration
  const getStatusBadge = (status) => {
    if (status === "Onboarded") {
      return {
        text: t("dashboard.verifiedProfessional"),
        icon: CheckCircle,
        bg: "bg-emerald-600",
        textColor: "text-white",
        border: "border border-emerald-700",
      };
    }
    return {
      text: t("dashboard.unverified"),
      icon: AlertCircle,
      bg: "bg-amber-500",
      textColor: "text-white",
      border: "border border-amber-600",
    };
  };

  const status = profileData.vetting_status;
  const badgeConfig = getStatusBadge(status);
  const StatusIcon = badgeConfig.icon;

  //  vetting history data
  const VETTING_HISTORY = profileData.vettingHistory
    ? profileData.vettingHistory.map((history) => ({
        stage: history.status,
        date: new Date(history.changedAt).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        }),
        note: history.additionalNote || t("dashboard.noAdditionalNotes"),
      }))
    : [];

  // applied jobs data
  const RECENT_APPLICATIONS = profileData.appliedJobs
    ? profileData.appliedJobs
        .slice(-3)
        .reverse()
        .map((job, index) => ({
          id: `${index + 1}`,
          title: job?.job?.description || t("dashboard.untitledJob"),
          client: job?.job?.created_by?.name || t("dashboard.unknownClient"),
          status: job.status.charAt(0).toUpperCase() + job.status.slice(1),
          date: job.appliedAt
            ? new Date(job.appliedAt).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
              })
            : "N/A",
        }))
    : [];

  //  current work data
  const ONGOING_WORK = profileData.currentJob
    ? {
        type: t("dashboard.activeJob"),
        client: profileData.currentJob.created_by?.name || t("dashboard.unknown"),
        phoneNumber: profileData.currentJob.created_by?.phoneNumber || "N/A",
        location: profileData.currentJob.location || t("dashboard.unknown"),
        category: profileData.currentJob.category || t("dashboard.unknown"),
        description:
          profileData.currentJob.description || t("dashboard.noDescription"),
        avatar:
          profileData.currentJob.created_by?.image ||
          "https://picsum.photos/100/100?random=10",
      }
    : null;

  // next booking data
  const NEXT_INTERVIEW = profileData.currentBooking
    ? {
        client: profileData.currentBooking.userData?.name || "Unknown",
        date: slotDateFormat(profileData.currentBooking.slotDate),
        time: profileData.currentBooking.slotTime,
        status: profileData.currentBooking.status,
        avatar:
          profileData.currentBooking.userData?.image ||
          "https://picsum.photos/100/100?random=11",
      }
    : null;

  // Stats cards data
  const stats = [
    {
      label: t("dashboard.stats.totalBookings"),
      value: dashData.bookings || dashData.appointments || 0,
      icon: Calendar,
      iconColor: "text-white",
      bgColor: "bg-slate-900",
      border: "border-slate-800",
      subtext: profileData.currentBooking
        ? t("dashboard.stats.upcomingBooking")
        : t("dashboard.stats.noUpcomingBookings"),
    },
    {
      label: t("dashboard.stats.clientsServed"),
      value: dashData.clients || 0,
      icon: Users,
      iconColor: "text-white",
      bgColor: "bg-slate-900",
      border: "border-slate-800",
      subtext: profileData.currentJob
        ? t("dashboard.stats.servingClient")
        : t("dashboard.stats.noActiveClients"),
    },
    {
      label: t("dashboard.stats.serviceFee"),
      value: profileData.fees
        ? `ETB ${caregiverNetAmount(profileData.fees).toFixed(0)}`
        : t("dashboard.stats.notSet"),
      icon: Wallet,
      iconColor: "text-white",
      bgColor: "bg-slate-900",
      border: "border-slate-800",
      subtext: t("dashboard.stats.currentRate"),
    },
    {
      label: t("dashboard.stats.locationCoverage"),
      value: profileData.subcity || t("dashboard.stats.notSpecified"),
      icon: MapPin,
      iconColor: "text-white",
      bgColor: "bg-slate-900",
      border: "border-slate-800",
      subtext: t("dashboard.stats.visibleClients"),
    },
  ];

  const RECENT_PAYOUTS = (payoutHistory || []).slice(0, 3);

  const getPayoutStatusClass = (status) => {
    switch (status) {
      case "PAID":
      case "SETTLED":
        return "bg-emerald-600 text-white";
      case "PENDING":
        return "bg-yellow-500 text-white";
      case "PROCESSING":
        return "bg-blue-600 text-white";
      default:
        return "bg-red-600 text-white";
    }
  };

  const isPayoutSettled = (status) => status === "PAID" || status === "SETTLED";

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            {t("dashboard.title")}
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            {t("dashboard.subtitle")}
          </p>
        </div>
        <div
          className={`flex items-center gap-2 px-4 py-2 rounded-2xl font-bold text-sm ${badgeConfig.bg} ${badgeConfig.textColor} ${badgeConfig.border}`}
        >
          <StatusIcon size={18} /> {badgeConfig.text}
        </div>
      </div>

      {/* Top Section: Stats Cards - 4 cards in a row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className={`${stat.bgColor} p-6 rounded-3xl border ${stat.border} shadow-lg shadow-slate-900/20 dark:shadow-none relative overflow-hidden group hover:shadow-xl transition-shadow`}
            >
              {/* Background Icon */}
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity text-white">
                <Icon size={64} />
              </div>

              {/* Content */}
              <div className="relative z-10">
                {/* Icon Container */}
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3 bg-white/10 text-white">
                  <Icon size={20} />
                </div>

                {/* Title */}
                <p className="text-white/70 font-medium text-sm">
                  {stat.label}
                </p>

                {/* Value */}
                <h3 className="text-3xl font-bold text-white mt-1 mb-2">
                  {stat.value}
                </h3>

                {/* Subtext */}
                <p className="text-xs text-white/60">{stat.subtext}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Contract Info Card */}
      {contractData && (
        <div className="bg-slate-900 rounded-[2.5rem] p-6 sm:p-8 text-white shadow-xl shadow-slate-900/20 dark:shadow-none border border-slate-800">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3">
                {t("dashboard.contract.activeContract")}
              </span>
              <h3 className="text-2xl font-bold">{t("dashboard.contract.monthlyContract")}</h3>
            </div>
            <span
              className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest text-white ${
                contractData.status === "ACTIVE"
                  ? "bg-emerald-600 border border-emerald-700"
                  : contractData.status === "PAUSED"
                    ? "bg-yellow-500 border border-yellow-600"
                    : "bg-gray-600 border border-gray-700"
              }`}
            >
              {contractData.status}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-white/70 text-sm mb-1">{t("contracts.client")}</p>
              <p className="font-bold text-lg">
                {contractData.client?.name || contractData.user?.name || "N/A"}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-sm mb-1">{t("contracts.rate")}</p>
              <p className="font-bold text-lg">
                {currency} {caregiverNetAmount(contractData.monthlySalary).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-sm mb-1">{t("contracts.startDate")}</p>
              <p className="font-bold">
                {contractData.startDate
                  ? new Date(contractData.startDate).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      },
                    )
                  : "N/A"}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-sm mb-1">{t("dashboard.contract.nextPayout")}</p>
              <p className="font-bold">
                {contractData.nextPayoutDate
                  ? new Date(contractData.nextPayoutDate).toLocaleDateString(
                      "en-US",
                      {
                        month: "short",
                        day: "2-digit",
                      },
                    )
                  : t("dashboard.contract.pending")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Payout History Section */}
      {RECENT_PAYOUTS.length > 0 && (
        <div className="bg-card dark:bg-dark-surface p-6 sm:p-8 rounded-[2.5rem] border border-gray-100 dark:border-dark-border shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-xl text-text dark:text-dark-text flex items-center gap-3">
              <Wallet
                size={20}
                className="text-green-600 dark:text-green-400"
              />
              {t("dashboard.recentPayouts.title")}
            </h3>
            <span className="text-sm text-gray-500 dark:text-dark-muted">
              Latest 3
            </span>
          </div>

          <div className="space-y-4">
            {RECENT_PAYOUTS.map((payout, index) => {
              const caregiverAmount = Number(payout.caregiverAmount ?? 0);

              return (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 sm:p-5 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-dark-border gap-3"
                >
                  <div className="flex-1">
                    <p className="font-bold text-text dark:text-dark-text">
                      {payout.periodStart
                        ? new Date(payout.periodStart).toLocaleDateString(
                            "en-US",
                            {
                              month: "long",
                              year: "numeric",
                            },
                          )
                        : "N/A"}
                    </p>
      
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="font-bold text-lg text-text dark:text-dark-text">
                        {currency} {caregiverAmount.toLocaleString()}
                      </p>
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-bold ${getPayoutStatusClass(payout.status)}`}
                      >
                        {payout.status}
                      </span>
                    </div>
                    {isPayoutSettled(payout.status) && (
                      <CheckCircle
                        size={20}
                        className="text-emerald-500 dark:text-emerald-400"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Middle Section: Ongoing Work & Next Appointment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Ongoing Work - Only show if caregiver has current job */}
        <div>
          {ONGOING_WORK ? (
            <div className="bg-slate-900 rounded-[2.5rem] p-6 sm:p-8 text-white relative overflow-hidden shadow-xl shadow-slate-900/20 dark:shadow-none transition-transform hover:scale-[1.01] border border-slate-800">
              <div className="absolute top-0 right-0 p-6 opacity-20">
                <Briefcase size={80} />
              </div>
              <span className="inline-block px-3 py-1 bg-primary text-white rounded-full text-[10px] font-bold uppercase tracking-wider mb-4">
                {t("dashboard.ongoingWork.title")}
              </span>
              <h3 className="text-xl font-bold mb-6">{t("dashboard.activeJob")}</h3>

              <div className="flex items-center gap-4 mb-6">
                <img
                  src={ONGOING_WORK.avatar}
                  alt={ONGOING_WORK.client}
                  className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30"
                  onError={(e) => {
                    e.target.src = "https://picsum.photos/100/100?random=10";
                  }}
                />
                <div>
                  <p className="font-bold text-lg">{ONGOING_WORK.client}</p>
                  <p className="text-white/70 text-sm">
                    {ONGOING_WORK.category}
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-sm">
                  <MapPin size={16} className="text-white/60" />
                  <span>{ONGOING_WORK.location}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone size={16} className="text-white/60" />
                  <span>{ONGOING_WORK.phoneNumber}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Briefcase size={16} className="text-white/60" />
                  <span className="truncate">{ONGOING_WORK.description}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-card dark:bg-dark-surface rounded-[2.5rem] p-6 sm:p-8 text-gray-600 dark:text-dark-muted relative overflow-hidden border border-gray-100 dark:border-dark-border">
              <span className="inline-block px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4">
                {t("dashboard.ongoingWork.title")}
              </span>
              <h3 className="text-xl font-bold mb-6 text-text dark:text-dark-text">
                {t("dashboard.ongoingWork.noActiveJob")}
              </h3>
              <p className="mb-6">
                {t("dashboard.ongoingWork.noActiveJobDesc")}
              </p>
              <button
                onClick={() => navigate("/caregiver/request/apply")}
                className="w-full py-4 bg-primary hover:bg-primary/90 text-white rounded-2xl font-bold text-sm transition-colors shadow-lg shadow-primary/20 dark:shadow-none"
              >
                {t("requests.applyNow")}
              </button>
            </div>
          )}
        </div>

        {/* Next Booking & Location - Only show booking if exists */}
        <div className="space-y-6">
          {/* Next Booking - Only show if exists */}
          {NEXT_INTERVIEW && (
            <div className="bg-slate-900 p-4 sm:p-6 rounded-[2rem] border border-slate-800 shadow-sm text-white">
              <h4 className="font-bold mb-4 flex items-center gap-2">
                <div className="text-blue-400">
                  <Calendar size={18} />
                </div>
                {["Work_Started", "work_started", "work-started", "In_Progress", "in_progress"].includes(NEXT_INTERVIEW.status)
                  ? t("dashboard.ongoingWork.title")
                  : t("dashboard.nextInterview.title")}
              </h4>
              <div className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-2xl">
                <img
                  src={NEXT_INTERVIEW.avatar}
                  alt={NEXT_INTERVIEW.client}
                  className="w-12 h-12 rounded-xl object-cover"
                  onError={(e) => {
                    e.target.src = "https://picsum.photos/100/100?random=11";
                  }}
                />
                <div className="flex-1">
                  <p className="font-bold text-sm">{NEXT_INTERVIEW.client}</p>
                  <p className="text-xs text-white/70">
                    {NEXT_INTERVIEW.date} • {NEXT_INTERVIEW.time}
                  </p>
                  <span
                    className={`text-xs px-2 py-1 rounded-full mt-1 inline-block font-bold text-white ${
                      ["Work_Started", "work_started", "work-started"].includes(NEXT_INTERVIEW.status)
                        ? "bg-emerald-600"
                        : ["In_Progress", "in_progress"].includes(NEXT_INTERVIEW.status)
                          ? "bg-blue-600"
                          : NEXT_INTERVIEW.status === "Completed" || NEXT_INTERVIEW.status === "completed"
                            ? "bg-gray-600"
                            : NEXT_INTERVIEW.status === "Cancelled" || NEXT_INTERVIEW.status === "cancelled"
                              ? "bg-red-600"
                              : "bg-yellow-500"
                    }`}
                  >
                    {NEXT_INTERVIEW.status}
                  </span>
                </div>
                <ChevronRight size={20} className="text-white/30" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section: Vetting & Applications */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Vetting Status Monitor */}
        <div className="bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] border border-slate-800 shadow-sm flex flex-col text-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <h3 className="font-bold text-xl flex items-center gap-3">
              <div className="text-blue-400">
                <History size={20} />
              </div>
              {t("dashboard.vettingHistory.title")}
            </h3>
            <span
              className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest text-white ${
                profileData.vetting_status === "Onboarded"
                  ? "bg-emerald-600 border border-emerald-700"
                  : profileData.vetting_status === "Active"
                    ? "bg-blue-600 border border-blue-700"
                    : profileData.vetting_status === "Rejected"
                      ? "bg-red-600 border border-red-700"
                      : "bg-yellow-500 border border-yellow-600"
              }`}
            >
              {profileData.vetting_status}
            </span>
          </div>
          <div className="space-y-6 relative ml-4">
            {/* Vertical Line */}
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-slate-700 -ml-[1px]"></div>

            {VETTING_HISTORY.length > 0 ? (
              VETTING_HISTORY.map((item, idx) => (
                <div
                  key={idx}
                  className="relative pl-8 animate-in fade-in slide-in-from-left-4"
                  style={{ animationDelay: `${idx * 150}ms` }}
                >
                  {/* Timeline Bullet */}
                  <div
                    className={`absolute left-0 top-0 w-4 h-4 rounded-full -ml-[9px] border-4 border-slate-900 shadow-sm ${
                      item.stage === "Onboarded"
                        ? "bg-emerald-500 scale-125"
                        : item.stage === "Active"
                          ? "bg-blue-500"
                          : item.stage === "Rejected"
                            ? "bg-red-500"
                            : "bg-gray-400"
                    }`}
                  ></div>

                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
                    <div>
                      <p className="text-sm font-bold">{item.stage}</p>
                    </div>
                    <span className="text-xs font-medium text-white/60">
                      {item.date}
                    </span>
                  </div>
                  <div className="p-4 bg-slate-800/50 rounded-2xl border border-slate-700">
                    <p className="text-xs text-white/70 leading-relaxed italic">
                      &quot;{item.note}&quot;
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-white/60">
                {t("dashboard.vettingHistory.noHistory")}
              </div>
            )}
          </div>
        </div>

        {/* Recently Applied Jobs */}
        <div className="bg-slate-900 p-6 sm:p-8 rounded-[2.5rem] border border-slate-800 shadow-sm text-white">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <h3 className="font-bold text-xl flex items-center gap-3">
              <div className="text-purple-400">
                <Briefcase size={20} />
              </div>
              {t("dashboard.appliedJobs.title")}
            </h3>
            <button
              onClick={() => navigate("/caregiver/application-status")}
              className="text-sm font-bold text-blue-400 hover:text-blue-300 hover:underline"
            >
              {t("dashboard.appliedJobs.viewAll")}
            </button>
          </div>
          <div className="space-y-4">
            {RECENT_APPLICATIONS.length > 0 ? (
              RECENT_APPLICATIONS.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 sm:p-5 bg-slate-800/50 border border-slate-700 rounded-3xl hover:bg-slate-800 transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white/60 group-hover:text-blue-400 transition-colors">
                      <FileText size={24} />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm">{job.title}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-white/70">
                          {job.client}
                        </span>
                        <span className="w-1 h-1 rounded-full bg-white/30"></span>
                        <span className="text-xs text-white/60">
                          {job.date}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white ${
                        job.status === "Accepted"
                          ? "bg-emerald-600 border border-emerald-700"
                          : job.status === "Rejected"
                            ? "bg-red-600 border border-red-700"
                            : "bg-yellow-500 border border-yellow-600"
                      }`}
                    >
                      {job.status}
                    </span>
                    <button className="p-1 hover:bg-slate-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreHorizontal size={16} className="text-white/60" />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-20 flex flex-col items-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mb-6 text-white/40">
                  <AlertCircle size={40} />
                </div>
                <p className="text-white/70 font-medium">
                  {t("dashboard.appliedJobs.noApplications")}
                </p>
                <button
                  onClick={() => navigate("/caregiver/request/apply")}
                  className="mt-4 text-sm font-bold text-blue-400 hover:text-blue-300 hover:underline"
                >
                  {t("requests.applyNow")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverDashboard;
